import { legalEntityTypeDraftFromValue, legalEntityTypeValue } from "@/data/legalEntityTypes";
import type { LegalEntityPayload } from "@/services/clientOnboarding";

import type { LegalEntityFormState, SignatoryFormState } from "../onboardingFormStateContext";
import { LEGAL_ENTITY_REQUIRED_FIELDS } from "../stepReadiness";

// Slot 0 is Entity Details, slot 1 Entity Contacts; the documents follow.
const DOCUMENT_FIELD_BY_SLOT = {
    2: ["formation_document", "formationDocumentUuid"],
    3: ["operating_document", "operatingDocumentUuid"],
    4: ["certificate_of_good_standing", "certificateOfGoodStandingUuid"]
} as const;

function resolvedLegalContact(
    state: LegalEntityFormState,
    signatory: SignatoryFormState
): { legalContactName: string; legalContactEmail: string } {
    if (state.legalContactSource === "primary") {
        return {
            legalContactName: state.primaryContactName.trim(),
            legalContactEmail: state.primaryContactEmail.trim()
        };
    }
    if (state.legalContactSource === "signatory") {
        return {
            legalContactName: signatory.signatoryName.trim(),
            legalContactEmail: signatory.signatoryEmail.trim()
        };
    }
    return {
        legalContactName: state.legalContactName.trim(),
        legalContactEmail: state.legalContactEmail.trim()
    };
}

function buildEntityDetailsPatch(state: LegalEntityFormState): LegalEntityPayload | null {
    const entityType = legalEntityTypeValue(state.entityType);
    if (
        !state.legalName.trim() ||
        !entityType ||
        !state.registeredAddress.trim() ||
        !state.companyNumber.trim() ||
        !state.jurisdiction
    ) {
        return null;
    }
    return {
        legalName: state.legalName.trim(),
        tradingName: state.tradingName.trim() || null,
        entityType,
        registeredAddress: state.registeredAddress.trim(),
        companyNumber: state.companyNumber.trim(),
        incorporationJurisdiction: state.jurisdiction
    };
}

function buildEntityContactsPatch(
    state: LegalEntityFormState,
    signatory: SignatoryFormState
): LegalEntityPayload | null {
    const legalContact = resolvedLegalContact(state, signatory);
    if (
        !state.primaryContactName.trim() ||
        !state.primaryContactEmail.trim() ||
        !legalContact.legalContactName ||
        !legalContact.legalContactEmail
    ) {
        return null;
    }
    return {
        primaryContactEmail: state.primaryContactEmail.trim(),
        primaryContactName: state.primaryContactName.trim(),
        ...legalContact
    };
}

export function buildLegalEntityStepPatch({
    state,
    signatory,
    slotIdx
}: {
    state: LegalEntityFormState;
    signatory: SignatoryFormState;
    slotIdx?: number;
}): LegalEntityPayload | null {
    if (slotIdx === 0) return buildEntityDetailsPatch(state);
    if (slotIdx === 1) return buildEntityContactsPatch(state, signatory);
    if (slotIdx !== undefined && slotIdx in DOCUMENT_FIELD_BY_SLOT) {
        const [documentKey, payloadKey] = DOCUMENT_FIELD_BY_SLOT[slotIdx as keyof typeof DOCUMENT_FIELD_BY_SLOT];
        const document = state.docs[documentKey];
        return document ? { [payloadKey]: document.documentUuid } : null;
    }

    const details = buildEntityDetailsPatch(state);
    const contacts = buildEntityContactsPatch(state, signatory);
    if (!details || !contacts) return null;
    const formation = state.docs.formation_document;
    const operating = state.docs.operating_document;
    const standing = state.docs.certificate_of_good_standing;
    if (!formation || !operating || !standing) return null;
    return {
        ...details,
        ...contacts,
        formationDocumentUuid: formation.documentUuid,
        operatingDocumentUuid: operating.documentUuid,
        certificateOfGoodStandingUuid: standing.documentUuid
    };
}

/** Payload keys owned by each sub-row, in `slots` order — 0 is Entity
 *  Details, 1 Entity Contacts, 2-4 the documents. Mirrors the scoping in
 *  `buildLegalEntityStepPatch` and the `fulfilment` keys the same slots
 *  carry in `spec/onboardingSpec.ts`; the three have to move together. */
const FIELDS_BY_SLOT: readonly (readonly string[])[] = [
    ["legalName", "entityType", "registeredAddress", "companyNumber", "incorporationJurisdiction"],
    ["primaryContactName", "primaryContactEmail", "legalContactName", "legalContactEmail"],
    ["formationDocumentUuid"],
    ["operatingDocumentUuid"],
    ["certificateOfGoodStandingUuid"]
];

/**
 * Human-readable names of the still-empty fields in the sub-row on screen
 * (or in the whole section when no sub-row is focused).
 *
 * The Save button goes disabled the moment `buildLegalEntityStepPatch`
 * returns null, and until now said nothing about why. The three document
 * slots are the common case: every text field can be filled and the step
 * still sits at In Progress because yaas-api counts the uploads too.
 */
export function legalEntityMissingFieldLabels({
    state,
    signatory,
    slotIdx
}: {
    state: LegalEntityFormState;
    signatory: SignatoryFormState;
    slotIdx?: number;
}): string[] {
    const legalContact = resolvedLegalContact(state, signatory);
    const values: Record<string, string> = {
        legalName: state.legalName,
        entityType: legalEntityTypeValue(state.entityType),
        registeredAddress: state.registeredAddress,
        companyNumber: state.companyNumber,
        incorporationJurisdiction: state.jurisdiction ?? "",
        primaryContactName: state.primaryContactName,
        primaryContactEmail: state.primaryContactEmail,
        legalContactName: legalContact.legalContactName,
        legalContactEmail: legalContact.legalContactEmail,
        formationDocumentUuid: state.docs.formation_document?.documentUuid ?? "",
        operatingDocumentUuid: state.docs.operating_document?.documentUuid ?? "",
        certificateOfGoodStandingUuid: state.docs.certificate_of_good_standing?.documentUuid ?? ""
    };
    const inScope = slotIdx === undefined ? null : new Set(FIELDS_BY_SLOT[slotIdx] ?? []);
    return LEGAL_ENTITY_REQUIRED_FIELDS.filter(
        ({ field }) => (!inScope || inScope.has(field)) && !values[field]?.trim()
    ).map(({ label }) => label);
}

export function legalEntityPatchIsDirty(patch: LegalEntityPayload | null, saved: LegalEntityPayload | null): boolean {
    if (!patch) return saved !== null;
    if (!saved) return true;
    return Object.entries(patch).some(([key, value]) => saved[key as keyof LegalEntityPayload] !== value);
}

export function hydrateLegalEntityFormState({
    initial,
    payload,
    orgLegalNameSeed,
    orgDisplayNameSeed
}: {
    initial: LegalEntityFormState;
    payload?: LegalEntityPayload;
    orgLegalNameSeed: string | null;
    orgDisplayNameSeed: string | null;
}): LegalEntityFormState {
    return {
        ...initial,
        legalName: payload?.legalName?.trim() || orgLegalNameSeed || "",
        tradingName: payload ? (payload.tradingName ?? "") : (orgDisplayNameSeed ?? ""),
        entityType: legalEntityTypeDraftFromValue(payload?.entityType),
        registeredAddress: payload?.registeredAddress ?? "",
        companyNumber: payload?.companyNumber ?? "",
        jurisdiction: payload?.incorporationJurisdiction ?? null,
        primaryContactName: payload?.primaryContactName ?? "",
        primaryContactEmail: payload?.primaryContactEmail ?? "",
        legalContactSource: null,
        legalContactName: payload?.legalContactName ?? "",
        legalContactEmail: payload?.legalContactEmail ?? "",
        docs: {
            formation_document: payload?.formationDocumentUuid
                ? {
                      documentUuid: payload.formationDocumentUuid,
                      filename: "On file"
                  }
                : null,
            operating_document: payload?.operatingDocumentUuid
                ? {
                      documentUuid: payload.operatingDocumentUuid,
                      filename: "On file"
                  }
                : null,
            certificate_of_good_standing: payload?.certificateOfGoodStandingUuid
                ? {
                      documentUuid: payload.certificateOfGoodStandingUuid,
                      filename: "On file"
                  }
                : null
        },
        lastSubmitted: payload && Object.keys(payload).length > 0 ? payload : null
    };
}
